import React, { useMemo, useState } from "react";

export default function ColumnChooserModal({ allColumns, selectedIds, onApply, onClose }) {
  const [ids, setIds] = useState(selectedIds || []);
  const [dragIndex, setDragIndex] = useState(null);

  const selectedList = useMemo(
    () => ids.map(id => allColumns.find(o => o.value === id)).filter(Boolean),
    [ids, allColumns]
  );
  const unselectedList = useMemo(
    () => allColumns.filter(o => !ids.includes(o.value)),
    [ids, allColumns]
  );

  const toggleId = (id) => {
    setIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const move = (from, to) => {
    setIds(prev => {
      const next = [...prev];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white text-black rounded-lg shadow-xl w-full max-w-3xl">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Choose Columns</h3>
          <button onClick={onClose} className="px-3 py-1 rounded border">Close</button>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Selected */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium">Visible (drag to reorder)</h4>
            </div>
            <ul className="border rounded min-h-[220px] p-2 bg-gray-50">
              {selectedList.map((opt, index) => (
                <li
                  key={opt.value}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (dragIndex === null || dragIndex === index) return;
                    move(dragIndex, index);
                    setDragIndex(null);
                  }}
                  className="flex items-center justify-between bg-white mb-2 rounded border px-3 py-2"
                >
                  <span className="text-sm">{opt.label}</span>
                  <div className="flex items-center gap-2">
                    <button
                      className="text-xs px-2 py-1 border rounded"
                      onClick={() => toggleId(opt.value)}
                    >
                      Hide
                    </button>
                    <span className="cursor-grab opacity-60">⋮⋮</span>
                  </div>
                </li>
              ))}
              {!selectedList.length && (
                <div className="text-sm text-gray-500 p-2">No columns visible</div>
              )}
            </ul>
          </div>

          {/* Available */}
          <div>
            <h4 className="font-medium mb-2">Available</h4>
            <ul className="border rounded min-h-[220px] p-2 bg-gray-50">
              {unselectedList.map(opt => (
                <li key={opt.value} className="flex items-center justify-between bg-white mb-2 rounded border px-3 py-2">
                  <span className="text-sm">{opt.label}</span>
                  <button
                    className="text-xs px-2 py-1 border rounded"
                    onClick={() => toggleId(opt.value)}
                  >
                    Show
                  </button>
                </li>
              ))}
              {!unselectedList.length && (
                <div className="text-sm text-gray-500 p-2">All columns are visible</div>
              )}
            </ul>
          </div>
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1 rounded border">Cancel</button>
          <button
            onClick={() => onApply(ids)}
            className="px-3 py-1 rounded border bg-black text-white"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
